import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  getExercisesByDayAndWeek,
  addExercisesByDayAndWeek,
  deleteExercisesByDayAndWeek,
  getSearchedExercises,
  updateSequence,
  updateSets,
  updateReps
} from '../../../redux/slices/admin/dragSlice';
 
import iconEditNote from '../../../images/icon-edit-note.svg';

import copyIcon from '../../../images/copy-icon.png';
import CopyExerciseModal from './CopyExerciseModal';
import NotesModal from './NotesModal';
import './styles.css';

const ManageProgramModal = ({ isOpen, handleCloseModal, selectedProgramWeeks, selectedProgramDays, selectedProgram }) => {
  const dispatch = useDispatch();
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [currentVal, setcurrentVal] = useState('A');
  const [selectedDay, setSelectedDay] = useState(null);
  const [weekDays, setWeekDays] = useState(null);
  const [assignedExercises, setAssignedExercises] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupedExercises, setGroupedExercises] = useState([]);
  const { ExercisesByDayAndWeek, SearchedExercises } = useSelector((state) => state.drag);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isCopyingWeek, setIsCopyingWeek] = useState(false);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false); 
  const [currentNote, setCurrentNote] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
    const openNotesModal = () => {
    setIsNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setIsNotesModalOpen(false);
    refresh();
  };

    const handleNoteUpdate = (note, id) => {
    setSelectedExercise(id);
    setCurrentNote(note);
    openNotesModal();
  }

  useEffect(() => {
    setAssignedExercises(ExercisesByDayAndWeek);
  }, [ExercisesByDayAndWeek]);

  useEffect(() => {
    setExercises(SearchedExercises);
  }, [SearchedExercises]);

  const generateWeeks = (numWeeks, numDays) => {
    let weeks = [];
    for (let week = 1; week <= numWeeks; week++) {
      let days = [];
      for (let day = 1; day <= numDays; day++) {
        days.push({ day_number: day });
      }
      weeks.push({ week_number: week, days });
    }
    return weeks;
  };

  const [weeks] = useState(generateWeeks(selectedProgramWeeks || 0, selectedProgramDays || 0));

  useEffect(() => {
    if (weeks.length > 0) {
      setSelectedWeek(weeks[0]);
      setSelectedDay(weeks[0].days[0]);
    }
  }, [weeks]);

  useEffect(() => {
    if (selectedWeek && selectedWeek.days) {
      setWeekDays(selectedWeek.days);
    } else {
      setWeekDays([]);
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (selectedDay) {
      dispatch(getExercisesByDayAndWeek({
        day_number: selectedDay.day_number,
        week_number: selectedWeek.week_number,
        training_program_id: selectedProgram.id,
      }));
    }
  }, [selectedDay, selectedProgram.id, selectedWeek, dispatch]);

  const refresh = () => {
  	dispatch(getExercisesByDayAndWeek({
        day_number: selectedDay.day_number,
        week_number: selectedWeek.week_number,
        training_program_id: selectedProgram.id,
    }));	
  }

  useEffect(() => {
    if (searchTerm.trim() !== '') {
      dispatch(getSearchedExercises(searchTerm));
    }
  }, [searchTerm, dispatch]);

useEffect(() => {
  const grouped = assignedExercises.reduce((acc, exercise) => {
    let group;
    if (exercise.sequence && /^[A-Za-z0-9]+$/.test(exercise.sequence)) {
      group = exercise.sequence[0];
    } else {
      group = currentVal ?? 'Uncategorized';
    }

    if (!acc[group]) acc[group] = [];
    acc[group].push(exercise);
    return acc;
  }, {});

  setGroupedExercises(
    Object.entries(grouped).map(([key, items]) => [key, [...items]])
  );
}, [assignedExercises]);


  const handleDragEnd = (result) => {
  const { draggableId, source, destination } = result;

  if (!destination) return;

  const sourceIndex = source.index;
  const exercise = exercises[sourceIndex];

  if (source.droppableId === 'exercises' && destination.droppableId.includes('group-')) {
    const destinationGroup = destination.droppableId.split('group-')[1];
    setAssignedExercises((prev) => [
      ...prev,
      { ...exercise, sequence: destinationGroup },
    ]);
    setExercises((prev) => prev.filter((item) => item.id !== exercise.id));
    dispatch(addExercisesByDayAndWeek({
      day_number: selectedDay.day_number,
      week_number: selectedWeek.week_number,
      training_program_id: selectedProgram.id,
      exercise_id: exercise.id,
      sequence: destinationGroup
    }));
  }

  if (source.droppableId.includes('group-') && destination.droppableId.includes('group-')) {
    const destinationGroup = destination.droppableId.split('group-')[1];
    const updatedExercises = assignedExercises.map((ex) =>
      ex.id == draggableId ? { ...ex, sequence: destinationGroup } : ex
    );
    setAssignedExercises(updatedExercises);
    dispatch(updateSequence({
      id: draggableId,
      sequence: destinationGroup
    }));
  }  
};

  const handleDeleteExercise = (id) => {
    setAssignedExercises(assignedExercises.filter((exercise) => exercise.id !== id));
    dispatch(deleteExercisesByDayAndWeek({
      day_number: selectedDay.day_number,
      week_number: selectedWeek.week_number,
      training_program_id: selectedProgram.id,
      exercise_id: id,
    }));
  };

  const handleSequenceChangeClick = (id, value) => {
     if (value.length>0) {
      setcurrentVal(value[0]);
    }
  };

  const handleSequenceChange = (id, value) => {   
    setAssignedExercises((prev) =>
      prev.map((exercise) =>
        exercise.id === id ? { ...exercise, sequence: value } : exercise
      )
    );

    if (value.trim() !== '') {
      const payload = { id, sequence: value };
      dispatch(updateSequence(payload));
    }
  };

  const handleSetsChange = (id, value) => {
    setAssignedExercises((prev) =>
      prev.map((exercise) =>
        exercise.id === id ? { ...exercise, sets: value } : exercise
      )
    );

    if (value.trim() !== '') {
      const payload = { id, sets: value };
      dispatch(updateSets(payload));
    }
  };

  const handleRepsChange = (id, value) => {
    setAssignedExercises((prev) =>
      prev.map((exercise) =>
        exercise.id === id ? { ...exercise, reps: value } : exercise
      )
    );

    // Only dispatch the update when the value is not empty
    if (value.trim() !== '') {
      const payload = { id, reps: value };
      dispatch(updateReps(payload));
    }
  };



  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

 

  const handleCloseCopyModal = () => {
    setIsCopyModalOpen(false);
  }

  const handleCopyDay = (day) => {
    setIsCopyingWeek(false);
    setIsCopyModalOpen(true);
    //setWeeks(["Week 1", "Week 2", "Week 3"]);
    //setDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]);
  };

  const handleCopyWeek = (week) => {
    setIsCopyingWeek(true);
    setIsCopyModalOpen(true);
    ///setWeeks(["Week 1", "Week 2", "Week 3"].filter(week => week !== selectedWeek));
  };



  if (!isOpen) return null;

  return (<>
    <div className="modal fade show manage-program-modal" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header justify-content-center">
            <h2>Manage Training Program</h2>
            <button className="close-button" onClick={handleCloseModal}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="weeks-sidebar">
              <h4>Weeks</h4>
              {weeks.map((week) => (
                <div
                  key={week.week_number}
                  onClick={() => setSelectedWeek(week)}
                  className={`list-item training-day-week ${selectedWeek === week ? 'selected' : ''}`}
                >
                  Week {week.week_number} <span className="copyIconSpan" onClick={() => handleCopyWeek(week.week_number)}><img className="copyIcon" src={copyIcon} /></span>
                </div>
              ))}
            </div>
            <div className="days-sidebar">
              {selectedWeek && (
                <>
                  <h4>Days</h4>
                  {selectedWeek.days.map((day) => (
                    <div
                      key={day.day_number}
                      onClick={() => setSelectedDay(day)}
                      className={`list-item training-day-week ${selectedDay === day ? 'selected' : ''}`}
                    >
                      Day {day.day_number} <span className="copyIconSpan" onClick={() => handleCopyDay(day.day_number)}><img className="copyIcon" src={copyIcon} /></span>
                    </div>
                  ))}
                </>
              )}
            </div>            






<div className="exercises-section">

  <DragDropContext onDragEnd={handleDragEnd}>
    <div className="assigned-exercises">
    

 {groupedExercises.length === 0 ? (
  <Droppable key="default-group" droppableId="group-1">
    {(provided) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className="group-container"
      >
        <h5>Default Group (1)</h5>
        <p className="placeholder-text">No exercises assigned yet. Drag and drop exercises here.</p>
        {provided.placeholder}
      </div>
    )}
  </Droppable>
) : (
  groupedExercises.map(([group, items]) => (
    <Droppable key={group} droppableId={`group-${group}`}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className="group-container"
        >
          <h5>{group}</h5>
          {items.map((exercise, index) => (
            <Draggable key={exercise.id} draggableId={exercise.id.toString()} index={index}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  className="draggable-item"
                >
                  <div className="exercise-name">{exercise.training_name || ''}</div>
                  <div className="input-group">
                    <label htmlFor={`sequence-${exercise.id}`} className="input-label">Sequence</label>
                    <input
                      id={`sequence-${exercise.id}`}
                      type="text"
                      value={exercise.sequence || ''}
                      placeholder="Sequence"
                      onClick={(e) => handleSequenceChangeClick(exercise.id, e.target.value)}
                      onChange={(e) => handleSequenceChange(exercise.id, e.target.value)}
                      className="sequence-input"
                      style={{width:'80px'}}
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor={`sets-${exercise.id}`} className="input-label">Sets</label>
                    <input
                      id={`sets-${exercise.id}`}
                      type="number"
                      value={exercise.sets || ''}
                      onChange={(e) => handleSetsChange(exercise.id, e.target.value)}
                      placeholder="Sets"
                      style={{width:'80px'}}
                      className="sets-input"
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor={`reps-${exercise.id}`} className="input-label">Reps</label>
                    <input
                      id={`reps-${exercise.id}`}
                      type="text"
                      value={exercise.reps || ''}
                      placeholder="Reps"
                      className="reps-input"
                      style={{width:'80px'}}
                      onChange={(e) => handleRepsChange(exercise.id, e.target.value)}
                    />
                  </div>


<div className="input-group" style={{ color:'grey', cursor:'pointer'}}>
   <img style={{marginTop:'15px', color:'grey', cursor:'pointer', height:'25px', minHeight:'25px', width:'25px', minWidth:'25px'}} src={iconEditNote} onClick={()=>handleNoteUpdate(exercise.notes, exercise.id)} alt="Edit Note" />
</div>

                  <div className="input-group">
                    <label htmlFor={`reps-${exercise.id}`} className="input-label"></label>
                    <span
                      className="delete-icon"
                      style={{ paddingTop: '20px' }}
                      onClick={() => handleDeleteExercise(exercise.id)}
                    >
                      &#x2715;
                    </span>
                  </div>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  ))
)}


 
    </div>
 
 



                <div className="exercises-list-container" style={{textAlign:'center'}}>
                  <input
                  style={{width:'97.5%', margin:'auto', marginBottom:'10px'}}
                    type="text"
                    className="search-input form-control"
                    placeholder="Search exercises..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <Droppable droppableId="exercises">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="exercises-list"
                      >
                        {exercises && exercises.map((exercise, index) => (
                          <Draggable key={exercise.id} draggableId={exercise.id.toString()} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="draggable-item"
                              >
                                {exercise.training_name || ''} ({exercise.category_name_display})
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </DragDropContext>
            </div>
          </div>
        </div>
      </div>
    </div>
 
 <NotesModal isOpen={isNotesModalOpen} onClose={closeNotesModal} currentNote={currentNote} selectedExercise={selectedExercise} refresh={refresh} />

     <CopyExerciseModal
        isOpen={isCopyModalOpen}
        handleClose={handleCloseCopyModal}
        isCopyingWeek={isCopyingWeek}
        availableWeeks={weeks}
        availableDays={weekDays}
        selectedDayData={selectedDay}
        selectedWeekData={selectedWeek}
        selectedProgram={selectedProgram}
      />
      </>
  );
};

export default ManageProgramModal;